import * as analytics from "@/modules/analytics";
import { type TrackProps } from "@/modules/analytics";
import config from "@/modules/config";
import { valueNames } from "@/modules/Constants";
import { type ArticleUrl, articleLinkOutcome } from "@/modules/articleLink";
import { openExternalUrl } from "@/modules/mcpAppLink";
import type ExperienceData from "@/modules/ExperienceData";
import type LinkData from "@/plugin/LinkData";
import { linkTypes } from "@/plugin/LinkData";
import type { ExperienceArticle } from "@/types/experience";
import { experienceStore } from "@/stores/experience/store";
import type { ExperienceModel } from "@/stores/experience/types";

/** A CTA event from the shop window (RootPlugin forwards it as is). */
interface LinkCtaPayload {
  region: string;
  link: LinkData;
  clusterName?: string;
  cindex?: number;
  index: number;
  idmerchant?: unknown;
}

interface ExitActionSheetPayload {
  region: string;
  clusterName: string;
  cindex: number;
  index: number;
}

function articleUrls(article: ExperienceArticle) {
  return article.urls as ArticleUrl[] | undefined;
}

function handleArticleLink(state: ExperienceModel, data: LinkCtaPayload) {
  const article = state.experienceData.getArticle(
    data.clusterName,
    data.cindex,
    data.index,
  );
  if (!article) {
    return false;
  }
  const outcome = articleLinkOutcome(articleUrls(article));

  if (outcome.kind === "sheet") {
    state.applicationData.setValue(valueNames.ACTIVE_ARTICLE, {
      clusterName: data.clusterName,
      cindex: data.cindex,
      index: data.index,
    });

    state.applicationData.setShowArticleActionSheet(true);

    analytics.track("Show Article Action Sheet", {
      pix_offset: state.experienceData.nowTime,
      pix_appregion: data.region,
      pix_id: article.id,
      pix_cluster: data.clusterName,
      pix_cindex: data.cindex,
      pix_index: data.index,
      pix_tag: data.link.tag,
    });
  } else {
    if (outcome.kind === "none") {
      return false;
    }
    const { url, idmerchant } = outcome;

    openExternalUrl(url, `PixmotoArticleWindow_${article.id}`);

    const trackingData: TrackProps = {
      pix_offset: state.experienceData.nowTime,
      pix_appregion: data.region,
      pix_id: article.id,
      pix_cluster: data.clusterName,
      pix_cindex: data.cindex,
      pix_index: data.index,
      pix_tag: data.link.tag,
    };

    if (idmerchant) {
      trackingData.pix_idmerchant = idmerchant;
    }

    analytics.track("Item CTA", trackingData);
  }
}

function handleFavoriteLink(state: ExperienceModel, data: LinkCtaPayload) {
  const article = state.favoritesData.getFavoriteArticle(data.index);
  const urls = article ? articleUrls(article) : undefined;
  if (!article || !urls || urls.length === 0) {
    return false;
  }

  const url = config.updatedLinkFromUrl(urls[0].url);
  openExternalUrl(url, `PixmotoArticleWindow_${article.id}`);
  analytics.track("Favorite CTA", {
    pix_offset: state.experienceData.nowTime,
    pix_appregion: data.region,
    pix_id: article.id,
    pix_cluster: data.clusterName,
    pix_index: data.index,
    pix_tag: data.link.tag,
  });
}

function handleExperienceLink(state: ExperienceModel, data: LinkCtaPayload) {
  let url = state.experienceData.url as string | null | undefined;
  if (!url) {
    return false;
  }

  url = config.updatedLinkFromUrl(url);
  openExternalUrl(url, "PixmotoExperienceWindow");
  analytics.track("Item CTA", {
    pix_offset: state.experienceData.nowTime,
    pix_appregion: data.region,
    pix_tag: data.link.tag,
  });
}

function handleUrlLink(state: ExperienceModel, data: LinkCtaPayload) {
  let url = data.link.url as string | null | undefined;
  if (!url) {
    return false;
  }

  url = config.updatedLinkFromUrl(url);
  // `_blank` gives every retailer link its own tab (a named window made the
  // "Where to buy" retailers share one); `noopener` keeps the retailer page
  // from reaching the player through window.opener.
  openExternalUrl(url, "_blank", { noopener: true });

  const trackingData: TrackProps = {
    pix_offset: state.experienceData.nowTime,
    pix_appregion: data.region,
    pix_tag: data.link.tag,
    pix_idmerchant: data.idmerchant,
  };

  const article = state.experienceData.getArticle(
    data.clusterName,
    data.cindex,
    data.index,
  );
  if (article) {
    trackingData.pix_id = article.id;
  }

  if (data.clusterName) {
    trackingData.pix_cluster = data.clusterName;
    trackingData.pix_cindex = data.cindex;
    trackingData.pix_index = data.index;
  }

  if (data.idmerchant) {
    trackingData.pix_idmerchant = data.idmerchant;
  }

  analytics.track("Item CTA", trackingData);
}

type LinkFunction = (experienceData: ExperienceData, link: LinkData) => void;

function handleFunctionLink(state: ExperienceModel, data: LinkCtaPayload) {
  (data.link.f as LinkFunction)(state.experienceData, data.link);

  analytics.track("Item CTA", {
    pix_offset: state.experienceData.nowTime,
    pix_appregion: data.region,
    pix_tag: data.link.tag,
  });
}

function handleEmptyLink(state: ExperienceModel, data: LinkCtaPayload) {
  analytics.track("Item CTA", {
    pix_offset: state.experienceData.nowTime,
    pix_appregion: data.region,
    pix_tag: data.link.tag,
  });
}

/** Runs a CTA; always notifies, also when the link has nothing to open. */
export function linkCta(data: unknown) {
  return experienceStore.dispatch(
    data as LinkCtaPayload,
    (state, payload: LinkCtaPayload) => {
      const link = payload.link;

      if (link.pause) {
        state.applicationData.setPauseVideo(true);
      }

      if (link.type === linkTypes.ARTICLE) {
        handleArticleLink(state, payload);
      } else if (link.type === linkTypes.EXPERIENCE) {
        handleExperienceLink(state, payload);
      } else if (link.type === linkTypes.FAVORITE) {
        handleFavoriteLink(state, payload);
      } else if (link.type === linkTypes.URL) {
        handleUrlLink(state, payload);
      } else if (link.type === linkTypes.FUNCTION) {
        handleFunctionLink(state, payload);
      } else if (link.type === linkTypes.EMPTY) {
        handleEmptyLink(state, payload);
      }
    },
  );
}

export function exitActionSheet(
  region: string,
  clusterName: string,
  cindex: number,
  index: number,
) {
  return experienceStore.dispatch(
    {
      region: region,
      clusterName: clusterName,
      cindex: cindex,
      index: index,
    },
    (state, data: ExitActionSheetPayload) => {
      const article = state.experienceData.getArticle(
        data.clusterName,
        data.cindex,
        data.index,
      );

      analytics.track("Hide Article Action Sheet", {
        pix_offset: state.experienceData.nowTime,
        pix_appregion: data.region,
        pix_id: article ? article.id : undefined,
        pix_cluster: data.clusterName,
        pix_cindex: data.cindex,
        pix_index: data.index,
      });

      state.applicationData.setShowArticleActionSheet(false);
    },
  );
}
